<template>
    <div class="flex flex-col h-full gap-2 px-2">
        <DxTreeList
            id="ai-tree-virtual"
            ref="listRef"
            :allow-column-reordering="true"
            :allow-column-resizing="true"
            :column-auto-width="meta.columnAutoWidth || true"
            :column-min-width="70"
            :data-source="dataSource"
            :filter-panel="{ visible: false }"
            :filter-row="{ visible: true }"
            :focused-row-enabled="true"
            :header-filter="{ visible: false }"
            :hover-state-enabled="true"
            :key-expr="meta.id"
            :parent-id-expr="meta.id_parent"
            :remote-operations="{ filtering: true, paging: true, sorting: true }"
            :root-value="meta.rootParentValue"
            :scrolling="{
                mode: 'virtual',
                scrollByContent: true,
                scrollByThumb: true,
            }"
            :show-borders="true"
            :show-column-lines="true"
            :show-row-lines="true"
            :word-wrap-enabled="meta?.wordWrapEnabled ?? true"
            class="h-0 grow w-full"
            column-resizing-mode="widget"
            dataStructure="plain"
            filter-mode="fullBranch"
            has-items-expr="has_items"
            @contentReady="onContentReady"
            @option-changed="onOptionChanged"
        >
            <dx-toolbar>
                <slot name="toolbarItems" />
                <dx-item :visible="true" location="after" name="refresh" widget="dxButton">
                    <dx-button
                        class="ai-button-base"
                        hint="Обновить"
                        icon="/infoenergo/assets/ai-refresh-base.svg"
                        @click="refresh"
                    />
                </dx-item>
            </dx-toolbar>
            <DxRemoteOperations :filtering="true" :group-paging="false" />
            <DxScrolling />
            <DxFilterRow :visible="'showFilterRow'" applyFilter="onClick" filter-mode="fullBranch"></DxFilterRow>
            <DxSelection :allowSelectAll="false" :mode="meta.selection || 'none'" :recursive="true" />
            <dx-column
                v-for="(item, index) in meta.columns"
                :key="index"
                :allowFiltering="item.allowFiltering"
                :caption="item.caption"
                :cell-template="item.cellTemplate"
                :data-field="item.dataField"
                :data-type="item.dataType"
                :fixed="item.fixed || false"
                :visible="item.visible"
                :width="item.width"
                :editor-options="{
                    ...item.editorOptions,
                    maxLength: item.maxLength,
                    showClearButton: isDate(item),
                }"
            />
            <template #firstColumnCellTemplate="{ data }">
                <div style="display: flex">
                    <component :is="data.data[meta.columns.find(c => c.dataField === data.column.name)?.svg]" />
                    <span style="padding-left: 10px">{{ data.value }}</span>
                </div>
            </template>
        </DxTreeList>
        <grid-footer :totalCount="totalCount"></grid-footer>
    </div>
</template>
<script setup>
/* Чтобы ограничить количество символов, которые можно добавить в поле ввода, необходимо в мету колонки прописать настройку 'maxLength: 250' (например, 250 символов)*/

import {
    DxColumn,
    DxFilterRow,
    DxItem,
    DxRemoteOperations,
    DxScrolling,
    DxSelection,
    DxToolbar,
    DxTreeList,
} from 'devextreme-vue/tree-list';
import { DxButton } from 'devextreme-vue';
import DataSource from 'devextreme/data/data_source';
import { aiTreeVirtualStore } from '@/components/ai-tree-virtual/ai-tree-virtual-store';
import { onMounted, ref, watch } from 'vue';
import GridFooter from '../grid-footer.vue';

/*
Чтобы слова в заголовках столбцов НЕ переносились необходимо добавить в мету этой таблицы настройку 'wordWrapEnabled: false,'
*/

const props = defineProps({
    meta: {
        type: Object,
        default: () => {
            return undefined;
        },
    },
});
const listRef = ref(null);

const treeListDataInit = () => {
    return {
        data: [],
        rootCount: 0,
        totalCount: 0,
    };
};

const treeListData = ref(treeListDataInit());

const pageSize = ref(50);
const pagesLoaded = ref(0);
const onOptionChanged = async e => {
    let loadOptions = null;
    let filter = listRef.value.instance.getCombinedFilter();
    for (const dxCol of listRef.value.instance._controllers.columns._columns) {
        if (dxCol.bufferedFilterValue) {
            const filterExpression = dxCol.calculateFilterExpression();
            filterExpression[2] = dxCol.bufferedFilterValue;
            filter = filterExpression;
        }
    }
    const sqlFilter = getSqlFilter(filter);

    switch (e.name) {
        // case  "dataSource":
        //   if (sqlFilter !== '') {
        //     loadOptions = {
        //       ... props.meta,
        //       mode: 'onScroll',
        //       skip: 0,
        //       sqlFilter
        //     }
        //     // listRef.value.instance.clearFilter()
        //     await loadData(loadOptions);
        //     pagesLoaded.value ++
        //     // listRef.value.instance.filter(filter)
        //   }
        //   break
        case 'paging':
            if (e.previousValue === undefined) {
                loadOptions = {
                    ...props.meta,
                    mode: 'onScroll',
                    skip: pageSize.value * pagesLoaded.value,
                    sqlFilter,
                };
                // listRef.value.instance.clearFilter()
                await loadData(loadOptions);
                pagesLoaded.value++;
            } else {
                if (e.value > e.previousValue) {
                    if (e.value > pagesLoaded.value - 1) {
                        loadOptions = {
                            ...props.meta,
                            mode: 'onScroll',
                            skip: pageSize.value * pagesLoaded.value,
                            sqlFilter,
                        };
                        // listRef.value.instance.clearFilter()
                        await loadData(loadOptions);
                        pagesLoaded.value++;
                    }
                }
            }
            break;
        case 'expandedRowKeys':
            if (e.value.length > 0) {
                const node = listRef.value.instance.getNodeByKey(e.value[e.value.length - 1]);
                // console.log('node:', node)
                if (node) {
                    const childs = treeListData.value.data.find(row => row[props.meta.id_parent] === node.key);
                    if (!childs) {
                        loadOptions = {
                            ...props.meta,
                            mode: 'onExpand',
                            parentIdValue: node.data[props.meta.id],
                            sqlFilter,
                        };
                        listRef.value.instance.beginCustomLoading();
                        await loadData(loadOptions);
                        listRef.value.instance.endCustomLoading();
                    }
                }
            }
            break;
        default:
            break;
    }
};
onMounted(async () => {
    await refresh();
});

const sqlCombinedFilter = value => {
    function toSqlCondition(condition) {
        if (Array.isArray(condition)) {
            if (condition.length === 3) {
                if (condition[1] === '=') {
                    if (condition[2] !== null) {
                        return `${condition[0]} = ${condition[2]}`;
                    }
                    return `${condition[0]} is null`;
                }
                if (condition[1] === 'contains') {
                    return `${condition[0]} like('%${condition[2]}%')`;
                }
                if (condition[1] === 'notcontains') {
                    return `${condition[0]} not like('%${condition[2]}%')`;
                }
                if (condition[1] === 'startswith') {
                    return `${condition[0]} like('${condition[2]}%')`;
                }
                if (condition[1] === 'endswith') {
                    return `${condition[0]} like('%${condition[2]}')`;
                }
                if (condition[1] === '<>') {
                    if (condition[2] !== null) {
                        return `${condition[0]} != ${condition[2]}`;
                    }
                    return `${condition[0]} is not null`;
                }
            }
        }
        return condition;
    }

    function itemIs(item) {
        if (typeof item === 'string') {
            if (item === 'or' || item === 'and') {
                return 'sqlOperator';
            }
        } else if (Array.isArray(item)) {
            if (item.length === 3) {
                if (itemIs(item[1]) === 'sqlOperator') {
                    return 'sqlGroup';
                } else {
                    return 'condition';
                }
            }
        }
    }

    // console.log(value)
    let resSql = '';
    if (itemIs(value) === 'sqlOperator') {
        resSql = `${resSql} ${value}`;
    } else if (itemIs(value) === 'condition') {
        resSql = `${resSql} ${toSqlCondition(value)}`;
    } else if (itemIs(value) === 'sqlGroup') {
        for (const item of value) {
            if (itemIs(item) === 'sqlOperator') {
                resSql = `${resSql} ${item}`;
            } else if (itemIs(item) === 'condition') {
                resSql = `${resSql} ${toSqlCondition(item)}`;
            } else {
                resSql = `${resSql} (${sqlCombinedFilter(item)})`;
            }
        }
    } else {
        return '';
    }

    return resSql;
};

function getSqlFilter(filter) {
    filter = filter ? filter : [];
    const sqlFilter = sqlCombinedFilter(filter);
    return sqlFilter;
}

const filters = ref([]);
const refresh = async () => {
    filters.value = [];
    let filter = listRef.value.instance.getCombinedFilter();
    for (const dxCol of listRef.value.instance._controllers.columns._columns) {
        if (dxCol.bufferedFilterValue) {
            const filterExpression = dxCol.calculateFilterExpression();
            filterExpression[2] = dxCol.bufferedFilterValue;
            filters.value.push(filterExpression);
        }
    }
    if (filters.value.length === 1) {
        filter = filters.value[0];
    }
    if (filters.value.length > 1) {
        filter = [];
        filters.value.forEach((item, index) => {
            filter.push(item);
            if (index < filters.value.length - 1) {
                filter.push('and');
            }
        });
    }
    // let filter = listRef.value.instance.getFilterExpression()

    listRef.value.instance.beginUpdate();
    // listRef.value.instance.filter(undefined)
    const sqlFilter = getSqlFilter(filter);
    // console.log('sqlFilter: ', sqlFilter)
    treeListData.value = treeListDataInit();
    const loadOptions = {
        ...props.meta,
        mode: 'onScroll',
        skip: 0,
        sqlFilter,
    };
    await loadData(loadOptions);
    pagesLoaded.value = 1;
    // listRef.value.instance.filter(filter)
    listRef.value.instance.endUpdate();
    // listRef.value.instance.filter(filter)
    //     listRef.value.instance.clearFilter()
    //     await loadData(loadOptions);
    //     pagesLoaded.value ++
    //     listRef.value.instance.filter(filter)
};

async function loadData(loadOptions) {
    treeListData.value = await aiTreeVirtualStore(props.meta.api.select, loadOptions, treeListData.value);
    // console.log('data:', treeListData.value.data)
}

const onContentReady = e => {};

const isDate = (column) => column.dataType === 'date' || column.dataType === 'datetime';

const dataSource = new DataSource({
    store: {
        data: treeListData.value.data,
        key: props.meta.id,
        type: 'array',
    },
});
watch(treeListData, () => {
    if (listRef.value.instance) {
        listRef.value.instance.option('dataSource', treeListData.value.data);
        //listRef.value.instance.repaintRows();
        // listRef.value.instance.repaint();
        // listRef.value.instance.refresh();
    }
});
defineExpose({
    listRef,
    refresh,
});
</script>
<script>
export default {
    name: 'ai-tree-virtual',
};
</script>

<style scoped>
:deep(.dx-menu-item-content) {
    display: flex;
    align-items: center;
}

:deep(.dx-row-removed > td) {
    background-color: white;
    text-decoration: line-through;
}

:deep(.dx-clear-button-area) {
    margin-left: 8px !important; /* Внешний отступ от поля ввода */
}
</style>
